import React, { useState } from 'react';
import { ROLE } from '../../common/role';
import { X } from "lucide-react";
import { summaryApi } from '../../common';
import { toast } from 'react-toastify';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { authFetch } from '../../helpers/authFetch';

function ChangeUserRole({firstName, lastName, email, role, userId, onClose, fetchAllUsers}) {
    const { colors } = useTheme();
    const [userRole, setUserRole] = useState(role);
    const [loading, setLoading] = useState(false);

    const handleOnChangeSelect = (e) => {
        setUserRole(e.target.value);
    };

    const updateUserRole = async() => {
        setLoading(true);
        try {
            const response = await authFetch(summaryApi.updateUser.url,{
                method: summaryApi.updateUser.method,
                
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    userId: userId,
                    role: userRole
                })
            });

            const responseData = await response.json();
            
            if (responseData.success) {
                toast.success(responseData.message || "Role updated successfully"); 
                await fetchAllUsers();   
                onClose();        
            }

            if (responseData.error) {
                toast.error(responseData.message || "Failed to update role");
            }
        } catch (error) {
            toast.error("Failed to update role");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
            <div 
                className="rounded-xl shadow-2xl w-full max-w-md transform transition-all hover:shadow-3xl border"
                style={{
                    backgroundColor: colors.bgSecondary,
                    borderColor: colors.border,
                    animation: 'slideUp 0.3s ease-out'
                }}
            >
                {/* Header */}
                <div 
                    className="flex items-center justify-between p-6 border-b"
                    style={{ 
                        borderColor: colors.border,
                        background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.accent}15 100%)`
                    }}
                >
                    <h2 className="text-xl font-bold" style={{ color: colors.text }}>
                        Change User Role
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg transition-all hover:scale-110"
                        style={{ 
                            backgroundColor: colors.bg,
                            color: colors.primary
                        }}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-5">
                    <div>
                        <p className="text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
                            Full Name
                        </p>
                        <p className="text-base font-semibold" style={{ color: colors.text }}>
                            {firstName} {lastName}
                        </p>
                    </div>

                    <div>
                        <p className="text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
                            Email Address
                        </p>
                        <p className="text-base font-semibold break-all" style={{ color: colors.text }}>
                            {email}
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold mb-3" style={{ color: colors.text }}>
                            Select New Role
                        </label>
                        <select 
                            value={userRole} 
                            onChange={handleOnChangeSelect}
                            className="w-full px-4 py-3 rounded-lg outline-none focus:ring-2 transition-all font-medium"
                            style={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                border: `2px solid ${colors.border}`,
                                focusColor: colors.primary,
                                boxShadow: `0 0 0 3px ${colors.primary}20`
                            }}
                        >
                            {Object.values(ROLE).map((item, index) => (
                                <option key={index} value={item}>
                                    {item}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Footer */}
                <div 
                    className="flex gap-3 p-6 border-t"
                    style={{ borderColor: colors.border }}
                >
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-3 font-medium rounded-lg transition-all hover:scale-105"
                        style={{
                            backgroundColor: colors.bg,
                            color: colors.text,
                            border: `1px solid ${colors.border}`
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={updateUserRole}
                        disabled={loading}
                        className="flex-1 px-4 py-3 text-white font-medium rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
                        style={{
                            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                        }}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center gap-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Updating...
                            </span>
                        ) : (
                            'Update Role'
                        )}
                    </button>
                </div>
            </div>

            <style jsx>{`
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
                
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
            `}</style>
        </div>
    );
}

export default ChangeUserRole;
